import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building, Building2, DollarSign, CreditCard, Clock, Upload, Save, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    company_name: "",
    registration_number: "",
    address: "",
    city: "",
    postal_code: "",
    headquarters: "",
    email: "",
    phone: "",
    website: "",
    vat_number: "",
    payment_terms: "Net 14 days",
    f_tax_certificate: true,
    rate_card: {
      daily_rate_labour: 0,
      daily_rate_technical: 0,
      daily_rate_labour_holiday: 0,
      daily_rate_technical_holiday: 0,
      hourly_rate_overtime: 0
    }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/company-profile`);
      setProfile(response.data);
      setFormData({
        company_name: response.data.company_name || "",
        registration_number: response.data.registration_number || "",
        address: response.data.address || "",
        city: response.data.city || "",
        postal_code: response.data.postal_code || "",
        headquarters: response.data.headquarters || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        website: response.data.website || "",
        vat_number: response.data.vat_number || "",
        payment_terms: response.data.payment_terms || "Net 14 days",
        f_tax_certificate: response.data.f_tax_certificate !== false,
        rate_card: {
          daily_rate_labour: response.data.rate_card?.daily_rate_labour || 0,
          daily_rate_technical: response.data.rate_card?.daily_rate_technical || 0,
          daily_rate_labour_holiday: response.data.rate_card?.daily_rate_labour_holiday || 0,
          daily_rate_technical_holiday: response.data.rate_card?.daily_rate_technical_holiday || 0,
          hourly_rate_overtime: response.data.rate_card?.hourly_rate_overtime || 0
        }
      });
    } catch (error) {
      toast.error("Failed to load company profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`${API}/company-profile`, formData);
      setProfile(response.data);
      toast.success("Company profile updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only PNG, JPEG, and WebP images are allowed");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be smaller than 2MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Content = reader.result.split(",")[1];
        
        const response = await axios.post(`${API}/company-profile/logo`, {
          filename: file.name,
          content: base64Content,
          content_type: file.type
        });
        
        setProfile(prev => ({
          ...prev,
          logo_file_id: response.data.file_id,
          logo_data_url: response.data.logo_data_url
        }));
        toast.success("Logo uploaded successfully");
        setUploadingLogo(false);
      };
      reader.onerror = () => {
        toast.error("Failed to read file");
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
      </div>
    );
  }

  return (
    <div data-testid="company-profile-page">
      <div className="flex items-center gap-3 mb-2">
        <Building className="w-8 h-8 text-[#3B82F6]" />
        <h1 className="text-4xl font-bold text-[#333333]">COMPANY PROFILE</h1>
      </div>
      <p className="text-[#52525B] mb-8">Manage your company information and rate card</p>

      <form onSubmit={handleSave}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Company Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#F37021]" />
                COMPANY INFORMATION
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Logo Upload */}
              <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg">
                <div className="w-24 h-24 bg-white rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                  {profile?.logo_data_url ? (
                    <img 
                      src={profile.logo_data_url}
                      alt="Company Logo"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <Building className="w-8 h-8" />
                      <span className="text-xs mt-1">No Logo</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700 mb-2">Company Logo</p>
                  <p className="text-xs text-gray-500 mb-3">PNG, JPEG or WebP (max 2MB)</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="logo-upload-input"
                  />
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    data-testid="logo-upload-button"
                  >
                    {uploadingLogo ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Upload Logo</>
                    )}
                  </Button>
                </div>
              </div>

              <div>
                <Label className="input-label">Company Name</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Gavs Bemanning AB"
                  data-testid="company-name-input"
                />
              </div>

              <div>
                <Label className="input-label">Registration Number (Org.nr)</Label>
                <Input
                  value={formData.registration_number}
                  onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                  placeholder="559029-6595"
                  data-testid="registration-number-input"
                />
              </div>

              <div>
                <Label className="input-label">Street Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Stationsallén 3"
                  data-testid="address-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Postal Code</Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="33230"
                    data-testid="postal-code-input"
                  />
                </div>
                <div>
                  <Label className="input-label">City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="GISLAVED"
                    data-testid="city-input"
                  />
                </div>
              </div>
              
              <div>
                <Label className="input-label">Headquarters (County)</Label>
                <Input
                  value={formData.headquarters}
                  onChange={(e) => setFormData({ ...formData, headquarters: e.target.value })}
                  placeholder="Jönköpingslän"
                  data-testid="headquarters-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="info@company.se"
                    data-testid="company-email-input"
                  />
                </div>
                <div>
                  <Label className="input-label">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+46 XX XXX XX XX"
                    data-testid="company-phone-input"
                  />
                </div>
              </div>
              
              <div>
                <Label className="input-label">Website</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="www.gavsbemanning.se"
                  data-testid="website-input"
                />
              </div>

              <div>
                <Label className="input-label">VAT Number (Moms.nr)</Label>
                <Input
                  value={formData.vat_number}
                  onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                  placeholder="SE556XXXXXXXX01"
                  data-testid="vat-number-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Default Payment Terms</Label>
                  <Input
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    placeholder="Net 14 days"
                    data-testid="payment-terms-input"
                  />
                </div>
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="f_tax_certificate"
                    checked={formData.f_tax_certificate}
                    onChange={(e) => setFormData({ ...formData, f_tax_certificate: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-[#F37021] focus:ring-[#F37021]"
                    data-testid="f-tax-checkbox"
                  />
                  <Label htmlFor="f_tax_certificate" className="text-sm">
                    Holds F-Tax Certificate
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rate Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#F37021]" />
                RATE CARD
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-700">
                  These rates will be used to automatically calculate quotes for worker requests.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="input-label flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-blue-600" />
                    Labour — Daily Rate (SEK)
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.rate_card.daily_rate_labour}
                      onChange={(e) => setFormData({
                        ...formData,
                        rate_card: { ...formData.rate_card, daily_rate_labour: parseFloat(e.target.value) || 0 }
                      })}
                      className="pl-12"
                      placeholder="0.00"
                      data-testid="rate-labour-input"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">SEK</span>
                  </div>
                </div>

                <div>
                  <Label className="input-label flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    Technical — Daily Rate (SEK)
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.rate_card.daily_rate_technical}
                      onChange={(e) => setFormData({
                        ...formData,
                        rate_card: { ...formData.rate_card, daily_rate_technical: parseFloat(e.target.value) || 0 }
                      })}
                      className="pl-12"
                      placeholder="0.00"
                      data-testid="rate-technical-input"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">SEK</span>
                  </div>
                </div>

                <div>
                  <Label className="input-label flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-blue-600" />
                    Labour — Holiday Daily Rate (SEK)
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.rate_card.daily_rate_labour_holiday}
                      onChange={(e) => setFormData({
                        ...formData,
                        rate_card: { ...formData.rate_card, daily_rate_labour_holiday: parseFloat(e.target.value) || 0 }
                      })}
                      className="pl-12"
                      placeholder="0.00"
                      data-testid="rate-labour-holiday-input"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">SEK</span>
                  </div>
                </div>

                <div>
                  <Label className="input-label flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    Technical — Holiday Daily Rate (SEK)
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.rate_card.daily_rate_technical_holiday}
                      onChange={(e) => setFormData({
                        ...formData,
                        rate_card: { ...formData.rate_card, daily_rate_technical_holiday: parseFloat(e.target.value) || 0 }
                      })}
                      className="pl-12"
                      placeholder="0.00"
                      data-testid="rate-technical-holiday-input"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">SEK</span>
                  </div>
                </div>

                <div>
                  <Label className="input-label flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-600" />
                    Overtime — Hourly Rate (SEK)
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.rate_card.hourly_rate_overtime}
                      onChange={(e) => setFormData({
                        ...formData,
                        rate_card: { ...formData.rate_card, hourly_rate_overtime: parseFloat(e.target.value) || 0 }
                      })}
                      className="pl-12"
                      placeholder="0.00"
                      data-testid="rate-overtime-input"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">SEK</span>
                  </div>
                </div>
              </div>

              {/* Rate Summary */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-semibold text-gray-700 mb-3">Current Rates Summary</p>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Labour (per day)</span>
                    <span className="font-semibold text-[#333333]">
                      {formData.rate_card.daily_rate_labour.toLocaleString('sv-SE')} SEK
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Technical (per day)</span>
                    <span className="font-semibold text-[#333333]">
                      {formData.rate_card.daily_rate_technical.toLocaleString('sv-SE')} SEK
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Labour Holiday (per day)</span>
                    <span className="font-semibold text-[#333333]">
                      {formData.rate_card.daily_rate_labour_holiday.toLocaleString('sv-SE')} SEK
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Technical Holiday (per day)</span>
                    <span className="font-semibold text-[#333333]">
                      {formData.rate_card.daily_rate_technical_holiday.toLocaleString('sv-SE')} SEK
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-t pt-2 mt-2">
                    <span className="text-sm text-gray-600">Overtime (per hour)</span>
                    <span className="font-semibold text-[#333333]">
                      {formData.rate_card.hourly_rate_overtime.toLocaleString('sv-SE')} SEK
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <Button 
            type="submit" 
            className="bg-[#F37021] hover:bg-[#D95D15] text-white px-8"
            disabled={saving}
            data-testid="save-profile-button"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Changes</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CompanyProfile;
